import React, { useCallback, useContext, useMemo } from 'react'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import LEOfferCard from './LEOfferCard'
import TourOfferCard from './TourOfferCard'
import TourV2OfferCard from './TourV2OfferCard'
import CruiseOfferCard from './CruiseOfferCard'
import BedbankOfferCard from './BedbankOfferCard'
import BundleAndSaveOfferCard from './BundleAndSaveOfferCard'
import offerPageURL from 'lib/offer/offerPageURL'
import VillaOfferCard from './VillaOfferCard'
import { isBedbankOffer, isBundleOffer, isCruiseOffer, isExperienceOffer, isLEHotel, isTourV1Offer, isTourV2Offer, isVillaOffer } from 'lib/offer/offerTypes'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import ExperienceOfferCard from './ExperienceOfferCard'

interface Props {
  idx?: number;
  className?: string;
  offer: App.AnyOffer;
  additionalOfferUrlParams?: string;
  selectedDatesAndRoomsFilter?: App.OfferListFilters;
  /**
   * @default card-default
   */
  variant?: 'card-default' | 'card-opaque' | 'card-locked'
  /**
   * @default small
   */
  size?: 'small' | 'medium';
  /**
   * Whether or not the offer tile should load it's image without
   * waiting for the tile to be in view.
   */
  eagerLoadImage?: boolean;
}

function OfferCardMapper({
  offer,
  idx,
  className,
  additionalOfferUrlParams,
  selectedDatesAndRoomsFilter,
  variant = 'card-default',
  size = 'small',
  eagerLoadImage,
}: Props) {
  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  const onClick = useCallback(() => {
    if (idx !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer,
        position: idx,
        key: EventDataKey.ProductClick,
      })
    }
  }, [idx, offer, dispatchOfferListEvent])

  const offerUrl = useMemo(() => {
    return offerPageURL(offer) + (additionalOfferUrlParams ? `?${additionalOfferUrlParams}` : '')
  }, [offer, additionalOfferUrlParams])

  const cardVariant = variant === 'card-opaque' ? 'opaque' : 'default'

  if (isLEHotel(offer)) {
    return <LEOfferCard
      offer={offer}
      onClick={onClick}
      className={className}
      offerUrl={offerUrl}
      variant={cardVariant}
      size={size}
      selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
      eagerLoadImage={eagerLoadImage}
    />
  } else if (isBundleOffer(offer)) {
    return <BundleAndSaveOfferCard
      offer={offer}
      onClick={onClick}
      className={className}
      offerUrl={offerUrl}
      variant={cardVariant}
      size={size}
      eagerLoadImage={eagerLoadImage}
    />
  } else if (isBedbankOffer(offer)) {
    return <BedbankOfferCard
      offer={offer}
      onClick={onClick}
      className={className}
      offerUrl={offerUrl}
      variant={cardVariant}
      size={size}
      selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
      eagerLoadImage={eagerLoadImage}
    />
  } else if (isTourV1Offer(offer)) {
    return <TourOfferCard
      offer={offer}
      onClick={onClick}
      className={className}
      offerUrl={offerUrl}
      variant={cardVariant}
      size={size}
      eagerLoadImage={eagerLoadImage}
    />
  } else if (isTourV2Offer(offer)) {
    return <TourV2OfferCard
      offer={offer}
      onClick={onClick}
      className={className}
      offerUrl={offerUrl}
      variant={cardVariant}
      size={size}
      eagerLoadImage={eagerLoadImage}
    />
  } else if (isCruiseOffer(offer)) {
    return <CruiseOfferCard
      offer={offer}
      onClick={onClick}
      className={className}
      offerUrl={offerUrl}
      variant={cardVariant}
      size={size}
      eagerLoadImage={eagerLoadImage}
    />
  } else if (isVillaOffer(offer)) {
    return <VillaOfferCard
      offer={offer}
      onClick={onClick}
      className={className}
      offerUrl={offerUrl}
      variant={cardVariant}
      size={size}
      eagerLoadImage={eagerLoadImage}
    />
  } else if (isExperienceOffer(offer)) {
    return <ExperienceOfferCard
      id={offer.id}
      index={idx}
      variant={cardVariant}
      size={size}
      className={className}
      onClick={onClick}
      eagerLoadImage={eagerLoadImage}
    />
  }

  return null
}

export default OfferCardMapper
